import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, CheckCircle2, X } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

const BATCH_SIZE = 100;

interface SyncResult {
  updatedHours: number;
  closedPermanently: number;
  errors: number;
}

interface SyncState {
  isRunning: boolean;
  totalCafes: number;
  syncedCount: number;
  result: SyncResult | null;
}

interface SyncContextType {
  syncState: SyncState;
  startSync: () => void;
}

const SyncContext = createContext<SyncContextType | null>(null);

export function useSyncProgress() {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSyncProgress must be used within SyncProgressProvider');
  return ctx;
}

export function SyncProgressProvider({ children }: { children: ReactNode }) {
  // IMPORTANT: always render the SAME component type wrapping `children`,
  // regardless of auth state. Previously this swapped between
  // <SyncContext.Provider> (unauthenticated) and <SyncProgressProviderInner>
  // (authenticated) at the same tree position. When `user` flipped null→set on
  // sign-in, React saw a different element type and unmounted+remounted the
  // ENTIRE subtree below — including <Routes>/<Index>/<OnboardingFlow> — which
  // reset onboarding to screen 0 and trapped new signups in a loop.
  // SyncProgressProviderInner is harmless without a user: it has no mount-time
  // effects, and its sync logic only runs when an admin calls startSync().
  return <SyncProgressProviderInner>{children}</SyncProgressProviderInner>;
}

function SyncProgressProviderInner({ children }: { children: ReactNode }) {
  const [syncState, setSyncState] = useState<SyncState>({
    isRunning: false,
    totalCafes: 0,
    syncedCount: 0,
    result: null,
  });
  const [dismissed, setDismissed] = useState(false);
  const abortRef = useRef(false);
  const queryClient = useQueryClient();

  const startSync = useCallback(async () => {
    setDismissed(false);
    abortRef.current = false;

    // Get total cafe count first
    const { count } = await supabase
      .from('cafes')
      .select('id', { count: 'exact', head: true })
      .not('google_place_id', 'is', null)
      .eq('is_active', true);

    const totalCafes = count || 0;

    // Check for an incomplete sync to resume from
    const { data: incompleteSync } = await supabase
      .from('sync_logs')
      .select('id, started_at, updated_hours, closed_permanently, errors, changes')
      .is('completed_at', null)
      .order('started_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    let offset = 0;
    let logId: string | null = null;
    let totalUpdatedHours = 0;
    let totalClosedPermanently = 0;
    let totalErrors = 0;

    if (incompleteSync) {
      // Resume: count how many cafes were already synced during this run
      const { count: alreadySynced } = await supabase
        .from('cafes')
        .select('id', { count: 'exact', head: true })
        .not('google_place_id', 'is', null)
        .eq('is_active', true)
        .gte('google_last_synced_at', incompleteSync.started_at);

      offset = alreadySynced || 0;
      logId = incompleteSync.id;
      totalUpdatedHours = incompleteSync.updated_hours || 0;
      totalClosedPermanently = incompleteSync.closed_permanently || 0;
      totalErrors = incompleteSync.errors || 0;

      toast.info(`Resuming sync from café ${offset} of ${totalCafes}`);
    }

    setSyncState({
      isRunning: true,
      totalCafes,
      syncedCount: offset,
      result: null,
    });

    const url = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/sync-cafe-details`;

    try {
      while (true) {
        if (abortRef.current) break;

        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ offset, limit: BATCH_SIZE, logId }),
        });

        if (!res.ok) {
          throw new Error(`Batch failed with status ${res.status}`);
        }

        const data = await res.json();

        logId = data.logId || logId;
        totalUpdatedHours += data.updatedHours || 0;
        totalClosedPermanently += data.closedPermanently || 0;
        totalErrors += data.errors || 0;

        const newSyncedCount = data.nextOffset ?? (offset + (data.batchProcessed || 0));

        setSyncState((prev) => ({
          ...prev,
          syncedCount: Math.min(newSyncedCount, prev.totalCafes),
        }));

        if (data.done) {
          setSyncState((prev) => ({
            ...prev,
            isRunning: false,
            syncedCount: prev.totalCafes,
            result: {
              updatedHours: totalUpdatedHours,
              closedPermanently: totalClosedPermanently,
              errors: totalErrors,
            },
          }));
          queryClient.invalidateQueries({ queryKey: ['admin-sync-status'] });
          toast.success(`Sync complete — ${totalUpdatedHours} hours updated`);
          return;
        }

        offset = data.nextOffset;
      }
    } catch (err) {
      console.error('Sync batch error:', err);
      setSyncState((prev) => ({ ...prev, isRunning: false }));
      toast.error('Sync failed');
    }
  }, [queryClient]);

  const showWidget = (syncState.isRunning || syncState.result) && !dismissed;
  const progress = syncState.totalCafes > 0
    ? Math.round((syncState.syncedCount / syncState.totalCafes) * 100)
    : 0;

  return (
    <SyncContext.Provider value={{ syncState, startSync }}>
      {children}
      {showWidget && (
        <div className="fixed bottom-4 right-4 z-50 w-80 rounded-lg border bg-card shadow-lg p-4 space-y-2 animate-in slide-in-from-bottom-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {syncState.isRunning ? (
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              ) : (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              )}
              <span className="text-sm font-medium">
                {syncState.isRunning ? 'Google Places Sync' : 'Sync Complete'}
              </span>
            </div>
            <button
              onClick={() => {
                setDismissed(true);
                if (syncState.isRunning) {
                  abortRef.current = true;
                }
                if (!syncState.isRunning) {
                  setSyncState((prev) => ({ ...prev, result: null }));
                }
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {syncState.isRunning ? (
            <>
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {syncState.syncedCount} / {syncState.totalCafes} cafes synced ({progress}%)
              </p>
            </>
          ) : syncState.result ? (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">
                {syncState.result.updatedHours} hours updated · {syncState.result.closedPermanently} closures
                {syncState.result.errors > 0 ? ` · ${syncState.result.errors} errors` : ''}
              </p>
              <button
                className="text-xs text-primary hover:underline"
                onClick={() => {
                  setDismissed(true);
                  setTimeout(() => {
                    document.getElementById('sync-history')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
              >
                View full results →
              </button>
            </div>
          ) : null}
        </div>
      )}
    </SyncContext.Provider>
  );
}
